/**
 * SF-4652: a demo build's change set, grouped as Eva's chat card groups it (sf-react-app
 * ChangeSetCard: "{group} ({count})"), with the company's logo and colours, the locations on a map
 * (the search circle and a pin per location with a map point) and in a list, the users, and each survey with its questions and the problem it answers. Items
 * that changed since the version shown before are highlighted, and taken-out ones listed.
 * SF-4847: a set with `modules` also gets a Modules card, one row per module with its icon, off
 * rows greyed out. While the plan waits for review the person can take out modules, locations and
 * users (an X on each item with a remove_id); a set without `modules` (older builds) renders as
 * before, with no X.
 * SF-4856: a set with `sites` also gets a Sites to demo card: the best site, then each struggling
 * site with its problem, by name.
 */
import { Alert, Button, Card, Image, List, Space, Tag, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import type {
  ChangeSetGroup,
  ChangeSetModule,
  ChangeSetItem,
  DemoBuild,
  DemoChangeSet,
  DemoSite,
} from '../api/demoBuildsClient';
import { coloursOf, itemKey, logoOf, type ChangeSetDiff } from '../changeSetDiff';
import { AreaMap } from './AreaMap';
import { locationName, pinsOf } from '../locationPins';
import { ModuleIcon } from './moduleIcons';

/** SF-4847: taking items out. Absent: read only, no X. */
export interface Removals {
  /** remove_ids taken out on this page and not yet sent to Eva. */
  pending: string[];
  /**
   * remove_ids the person took out before (build.inputs.removed) that Eva's last version still
   * has (SF-4847 S6): struck through like pending ones, with no Undo (sf-api already holds them).
   */
  locked?: string[];
  /** A message, approval or cancel is on its way: X and Undo are shown but cannot be pressed. */
  disabled?: boolean;
  onRemove: (removeId: string) => void;
  onUndo: (removeId: string) => void;
}

/** The X, or the Undo for an item already taken out. Nothing when the item cannot be removed. */
function RemoveControl({
  removeId,
  label,
  removals,
}: {
  removeId: string | null | undefined;
  label: string;
  removals: Removals | null;
}) {
  if (!removals || !removeId || removals.locked?.includes(removeId)) return null;
  return removals.pending.includes(removeId) ? (
    <Button
      size="small"
      type="link"
      aria-label={`Undo taking out ${label}`}
      disabled={removals.disabled}
      onClick={() => removals.onUndo(removeId)}
    >
      Undo
    </Button>
  ) : (
    <Button
      size="small"
      type="text"
      icon={<CloseOutlined />}
      aria-label={`Take out ${label}`}
      disabled={removals.disabled}
      onClick={() => removals.onRemove(removeId)}
    />
  );
}

/**
 * SF-4856: a site's name when sf-chat-service sends none: its location item's name, read the same
 * way as the map pin's (locationName), so "York (North)" stays whole; else the ref.
 */
function siteName(groups: ChangeSetGroup[], ref: string): string {
  const item = groups.flatMap((g) => g.items ?? []).find((i) => i.ref === ref);
  const name = item ? locationName(item.summary).trim() : '';
  return name || ref;
}

/** SF-4856: the best site first, then each struggling site with its problem. */
function SitesCard({ sites, groups }: { sites: DemoSite[]; groups: ChangeSetGroup[] }) {
  const ordered = [
    ...sites.filter((s) => s.role === 'best'),
    ...sites.filter((s) => s.role === 'struggling'),
  ];
  if (ordered.length === 0) return null;
  return (
    <Card size="small" title="Sites to demo">
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {ordered.map((s, i) => (
          <li key={i}>
            {s.role === 'best' ? 'Best site: ' : 'Struggling: '}
            {s.name?.trim() || siteName(groups, s.ref)}
            {s.region?.trim() ? ` (${s.region.trim()})` : ''}
            {s.role === 'struggling' && s.problem && (
              <div>
                <Typography.Text type="secondary">{s.problem}</Typography.Text>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

const isPending = (removals: Removals | null, removeId: string | null | undefined) =>
  !!removals &&
  !!removeId &&
  (removals.pending.includes(removeId) || !!removals.locked?.includes(removeId));

function ModulesCard({
  modules,
  removals,
  switched,
}: {
  modules: ChangeSetModule[];
  removals: Removals | null;
  /** Rows switched on or off since the version shown before. */
  switched: Map<string, 'on' | 'off'> | null;
}) {
  return (
    <Card size="small" title="Modules" data-testid="modules-card">
      <List
        size="small"
        dataSource={modules}
        rowKey={(m) => m.key}
        renderItem={(m) => {
          const pending = isPending(removals, m.remove_id);
          const change = switched?.get(m.key);
          return (
            <List.Item
              data-testid="module-row"
              data-on={m.on ? 'true' : 'false'}
              data-change={change ? `switched-${change}` : undefined}
              // Off rows are greyed out (SF-4847): they are listed so the person sees what is not shown.
              style={{ ...(change ? HIGHLIGHT : {}), ...(m.on ? {} : { opacity: 0.5 }) }}
              extra={<RemoveControl removeId={m.remove_id} label={m.name} removals={removals} />}
            >
              <Space>
                <ModuleIcon moduleKey={m.key} />
                <Typography.Text type={m.on ? undefined : 'secondary'} delete={pending}>
                  {m.name}
                </Typography.Text>
                {m.base && <Tag>Always on</Tag>}
                {!m.on && <Tag>Off</Tag>}
                {pending && <Tag color="red">Taking out</Tag>}
                {change && (
                  <Tag color="gold">{change === 'on' ? 'Switched on' : 'Switched off'}</Tag>
                )}
              </Space>
            </List.Item>
          );
        }}
      />
    </Card>
  );
}

const HIGHLIGHT = { background: '#fffbe6', borderLeft: '3px solid #faad14', paddingLeft: 8 };
const noPick = () => undefined;

const isLocations = (g: ChangeSetGroup) => g.group.toLowerCase().startsWith('location');

function ItemBody({
  item,
  company,
  pending,
}: {
  item: ChangeSetItem;
  company: boolean;
  pending: boolean;
}) {
  const colours = company ? coloursOf(item.details) : [];
  const logo = company ? logoOf(item.details) : null;
  const questions = (item.details ?? []).filter((d) => d.startsWith('Question: '));
  const other = (item.details ?? []).filter(
    (d) => !d.startsWith('Question: ') && !(company && /^(Colours|Logo): /.test(d)),
  );
  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Typography.Text delete={pending}>{item.summary}</Typography.Text>
      {logo && (
        <Image src={logo} alt="Logo" height={48} preview={false} data-testid="company-logo" />
      )}
      {colours.length > 0 && (
        <Space wrap size={4}>
          {colours.map((c) => (
            <Tag key={c.name} data-testid="company-colour">
              <span
                style={{ display: 'inline-block', width: 12, height: 12, background: c.value }}
              />{' '}
              {c.name}: {c.value}
            </Tag>
          ))}
        </Space>
      )}
      {item.problem && (
        <Typography.Text type="secondary" data-testid="survey-reason">
          Why it is here: {item.problem}
        </Typography.Text>
      )}
      {questions.length > 0 && (
        <ol style={{ margin: 0, paddingLeft: 20 }} data-testid="survey-questions">
          {questions.map((q, i) => (
            <li key={i}>{q.slice('Question: '.length)}</li>
          ))}
        </ol>
      )}
      {other.map((d, i) => (
        <Typography.Text key={i} type="secondary">
          {d}
        </Typography.Text>
      ))}
    </Space>
  );
}

function GroupCard({
  group,
  build,
  diff,
  removals,
}: {
  group: ChangeSetGroup;
  build: DemoBuild;
  diff: ChangeSetDiff | null;
  removals: Removals | null;
}) {
  const items = Array.isArray(group.items) ? group.items : [];
  const removed = diff?.removed[group.group] ?? [];
  const company = group.group.toLowerCase() === 'company';
  const area = build.inputs?.area;
  return (
    <Card size="small" title={`${group.group} (${items.length})`} data-testid="change-set-group">
      {isLocations(group) && area && (
        <div style={{ marginBottom: 12 }}>
          <AreaMap
            centre={{ lat: area.lat, lng: area.lng }}
            radiusMiles={area.radius_miles}
            onPick={noPick}
            pins={pinsOf(items)}
          />
        </div>
      )}
      {/* A group Eva emptied has only "Taken out" rows; an empty List would show "No data". */}
      {items.length > 0 && (
        <List
          size="small"
          dataSource={items}
          rowKey={(item) => item.ref}
          renderItem={(item) => {
            const key = itemKey(group.group, item.ref);
            const mark = diff?.added.has(key) ? 'New' : diff?.changed.has(key) ? 'Changed' : null;
            const pending = isPending(removals, item.remove_id);
            return (
              <List.Item
                style={mark ? HIGHLIGHT : undefined}
                data-testid="change-set-item"
                data-change={mark ? mark.toLowerCase() : undefined}
                extra={
                  <RemoveControl
                    removeId={item.remove_id}
                    label={item.summary}
                    removals={removals}
                  />
                }
              >
                <Space align="start">
                  {mark && <Tag color={mark === 'New' ? 'green' : 'gold'}>{mark}</Tag>}
                  {pending && <Tag color="red">Taking out</Tag>}
                  <ItemBody item={item} company={company} pending={pending} />
                </Space>
              </List.Item>
            );
          }}
        />
      )}
      {removed.map((item) => (
        <div
          key={item.ref}
          data-testid="change-set-removed"
          style={{ ...HIGHLIGHT, borderLeftColor: '#ff4d4f' }}
        >
          <Tag color="red">Taken out</Tag>
          <Typography.Text delete>{item.summary}</Typography.Text>
        </div>
      ))}
    </Card>
  );
}

export interface ChangeSetViewProps {
  changeSet: DemoChangeSet;
  /** The version approve sends (the build's change_set_version). */
  version: number;
  build: DemoBuild;
  diff: ChangeSetDiff | null;
  /** SF-4847: given only while the person may take items out (ready for review, theirs). */
  removals?: Removals | null;
}

export const ChangeSetView: React.FC<ChangeSetViewProps> = ({
  changeSet,
  version,
  build,
  diff,
  removals = null,
}) => {
  const modules = Array.isArray(changeSet.modules) ? changeSet.modules : null;
  // An older set (no `modules`) has no remove_ids worth trusting: no X anywhere (SF-4847).
  const canRemove = modules ? removals : null;
  // With the Modules card, chat-service's one-line modules step group would say the same twice.
  const groups = (Array.isArray(changeSet.groups) ? changeSet.groups : []).filter(
    (g) => !(modules && g.group.toLowerCase() === 'modules'),
  );
  const total = groups.reduce((n, g) => n + (g.items?.length ?? 0), 0);
  // A group emptied by this version is not in the new set; still show what was taken out of it.
  const goneGroups = Object.keys(diff?.removed ?? {}).filter(
    (name) =>
      !groups.some((g) => g.group === name) && !(modules && name.toLowerCase() === 'modules'),
  );
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Text strong data-testid="change-set-summary">
        Version {version}. Changes in total: {total}
      </Typography.Text>
      {(changeSet.problems?.length ?? 0) > 0 && (
        <Card size="small" title="What the prospect told us">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {changeSet.problems!.map((p, i) => (
              <li key={i}>
                {p.text}
                {p.detail && (
                  <div>
                    <Typography.Text type="secondary">{p.detail}</Typography.Text>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
      {(changeSet.learnings?.length ?? 0) > 0 && (
        <Card size="small" title="What else we learned">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {changeSet.learnings!.map((l, i) => (
              <li key={i}>{l.text}</li>
            ))}
          </ul>
        </Card>
      )}
      {Array.isArray(changeSet.sites) && changeSet.sites.length > 0 && (
        <SitesCard sites={changeSet.sites} groups={groups} />
      )}
      {modules && (
        <ModulesCard modules={modules} removals={canRemove} switched={diff?.modules ?? null} />
      )}
      {groups.map((g) => (
        <GroupCard key={g.group} group={g} build={build} diff={diff} removals={canRemove} />
      ))}
      {goneGroups.map((name) => (
        <GroupCard
          key={name}
          group={{ group: name, count: 0, items: [] }}
          build={build}
          diff={diff}
          removals={null}
        />
      ))}
      {(changeSet.warnings?.length ?? 0) > 0 && (
        <Alert
          type="warning"
          showIcon
          message="Eva's notes on this set"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {changeSet.warnings!.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          }
        />
      )}
    </Space>
  );
};
